import React from 'react';
import { bool, string, node, oneOfType } from 'prop-types';
import classNames from 'classnames';
import { getRenderable } from '@fiverr-private/fit';
import TooltipIcon from '../TooltipIcon';
import Optional from './Optional';

import classes from './index.module.scss';

/**
 * @deprecated Please use FormControl from @fiverr-private/forms instead.
 */
const FormControl = ({
    className,
    id,
    label,
    isOptional,
    isSuccess,
    isError,
    disabled,
    hint,
    tooltipContent,
    children
}) => {
    const formControlClassName = classNames(
        classes.formControl,
        className
    );
    const labelClassNames = classNames(
        classes.label,
        'text-semi-bold'
    );
    const hintClassNames = classNames(
        classes.hint,
        'tbody-6',
        {
            [classes.success]: isSuccess,
            [classes.error]: isError
        }
    );
    const enrichedProps = { isSuccess, isError, disabled, id };

    return (
        <div className={formControlClassName}>
            <span className={classes.labelWrapper}>
                {label && <label className={labelClassNames} htmlFor={id}>{label}</label>}
                {isOptional && <Optional/>}
                {tooltipContent && <TooltipIcon content={tooltipContent}/>}
            </span>
            {React.Children.map(children, (child) => getRenderable(child, {}, enrichedProps))}
            <span className={hintClassNames}>{hint}</span>
        </div>
    );
};

FormControl.displayName = 'FormControl';

FormControl.propTypes = {
    /**
    * Custom classes you wish to pass to the component.
    */
    className: string,
    /**
     * The id of the form element, also used as the htmlFor attribute of the label.
     */
    id: string,
    /**
     * The form element label will be displayed above the form element.
     */
    label: oneOfType([string, node]),
    /**
     * Whether or not to display the "Optinal" indication besides the label.
     */
    isOptional: bool,
    /**
     * Whether or not the form element is disabled. Disabled form element is not clickable, or hoverable.
     */
    disabled: bool,
    /**
     * Whether or not the form element is in a suceessful status.
     */
    isSuccess: bool,
    /**
     * Whether or not the form element is in an error status.
     */
    isError: bool,
    /**
     * The form element hint will be displayed below the form element.
     */
    hint: oneOfType([string, node]),
    /**
     * The content of the tooltip. If this property is not defined, the tooltip will not appear.
     */
    tooltipContent: oneOfType([string, node]),
    /**
     * The child element that the FormControl will wrap.
     */
    children: node
};

FormControl.defaultProps = {
    isOptional: false,
    disabled: false,
    isSuccess: false,
    isError: false
};

export default FormControl;
